/**
 * Sync Service - client for the Billy-hosted profile sync API.
 *
 * Replaces the old Firebase backend. Design contract:
 * - localforage stays the PRIMARY store; the cloud is a best-effort mirror
 * - auth is a per-install sync code (UUIDv4) generated on first use and sent
 *   as a Bearer token; pasting the code into another device grants it access
 * - when VITE_SYNC_URL is absent everything here degrades to a no-op and the
 *   app runs purely locally (same graceful contract Firebase had)
 */

import localforage from 'localforage';
import errorLog from './errorLog.js';

// Safe to import under node --test: `import.meta.env` is simply undefined there.
const SYNC_URL = (import.meta.env && import.meta.env.VITE_SYNC_URL) || '';
const SYNC_CODE_KEY = 'looperSyncCode';

export const isSyncEnabled = Boolean(SYNC_URL);

/**
 * PURE: last-write-wins merge of two profile lists keyed by id.
 * The newer updatedAt wins; entries missing updatedAt lose against any that has one.
 *
 * @param {Array<object>} localList
 * @param {Array<object>} remoteList
 * @returns {{merged: Array<object>, remoteWonIds: Array<string>}}
 */
export function mergeProfiles(localList, remoteList) {
    const time = (p) => {
        const t = p && p.updatedAt ? Date.parse(p.updatedAt) : NaN;
        return Number.isNaN(t) ? 0 : t;
    };

    const byId = new Map();
    for (const p of localList || []) {
        if (p && p.id) byId.set(p.id, { profile: p, source: 'local' });
    }

    const remoteWonIds = [];
    for (const p of remoteList || []) {
        if (!p || !p.id) continue;
        const existing = byId.get(p.id);
        if (!existing || time(p) > time(existing.profile)) {
            const isRemoteWin = Boolean(existing);
            if (isRemoteWin) remoteWonIds.push(p.id);
            byId.set(p.id, { profile: p, source: 'remote' });
        }
    }

    return {
        merged: Array.from(byId.values(), (e) => e.profile),
        remoteWonIds
    };
}

/** Get (or lazily create) this install's sync code. Null when storage fails. */
export async function ensureSyncCode() {
    try {
        let code = await localforage.getItem(SYNC_CODE_KEY);
        if (!code) {
            code = crypto.randomUUID();
            await localforage.setItem(SYNC_CODE_KEY, code);
        }
        return code;
    } catch (error) {
        errorLog.capture(error, 'sync', { op: 'ensureSyncCode' });
        return null;
    }
}

function headers(code) {
    return { Authorization: `Bearer ${code}`, 'Content-Type': 'application/json' };
}

async function request(method, path, code, body) {
    const response = await fetch(`${SYNC_URL}${path}`, {
        method,
        headers: headers(code),
        body: body === undefined ? undefined : JSON.stringify(body)
    });
    if (!response.ok) {
        throw new Error(`Sync ${method} ${path} failed: HTTP ${response.status}`);
    }
    return response.json();
}

/** Pull every profile in this install's namespace. Returns {} on disabled/empty. */
export async function pullAll() {
    if (!isSyncEnabled) return {};
    try {
        const code = await ensureSyncCode();
        if (!code) return {};
        const data = await request('GET', '/api/profiles', code);
        return data.profiles && typeof data.profiles === 'object' ? data.profiles : {};
    } catch (error) {
        console.warn('[sync] pull failed:', error.message);
        errorLog.capture(error, 'sync', { op: 'pullAll' });
        return {};
    }
}

/** Mirror one saved profile. Fire-and-forget; failures never block local saves. */
export async function pushProfile(profile) {
    if (!isSyncEnabled || !profile || !profile.id) return false;
    try {
        const code = await ensureSyncCode();
        if (!code) return false;
        await request('PUT', `/api/profiles/${encodeURIComponent(profile.id)}`, code, profile);
        return true;
    } catch (error) {
        console.warn('[sync] push failed:', error.message);
        errorLog.capture(error, 'sync', { op: 'pushProfile', detail: profile.id });
        return false;
    }
}

/** Mirror one deletion. Best-effort, same as pushProfile. */
export async function deleteProfile(profileId) {
    if (!isSyncEnabled || !profileId) return false;
    try {
        const code = await ensureSyncCode();
        if (!code) return false;
        await request('DELETE', `/api/profiles/${encodeURIComponent(profileId)}`, code);
        return true;
    } catch (error) {
        console.warn('[sync] delete failed:', error.message);
        errorLog.capture(error, 'sync', { op: 'deleteProfile', detail: profileId });
        return false;
    }
}
